/**
 * useDashboardActions — all CRUD handlers for the Dashboard page.
 *
 * Extracted from DashboardPage.tsx to reduce the God Component.
 * Receives state setters via params; returns action callbacks.
 */

import { useCallback } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import {
  createTemplate,
  deleteTemplate,
  duplicateTemplate,
  updateTemplate,
  archiveTemplate,
  restoreTemplate,
  type TemplateMeta,
  type TemplateStatus,
  invalidateTemplatesCache,
} from "../utils/templateApi";
import { sendNotification } from "../utils/notificationsApi";
import { STATUS_CONFIG } from "../components/dashboard/dashboardConstants";
import type { UserRecord } from "../utils/userApi";

/* ================================================================
   Params interface
   ================================================================ */

interface ActionDeps {
  user: { username: string; displayName?: string } | null;
  templates: TemplateMeta[];
  setTemplates: React.Dispatch<React.SetStateAction<TemplateMeta[]>>;
  setActionLoading: React.Dispatch<React.SetStateAction<string | null>>;
  setDeleteConfirm: React.Dispatch<React.SetStateAction<string | null>>;
  setEditingId: React.Dispatch<React.SetStateAction<string | null>>;
  setShowCreateForm: React.Dispatch<React.SetStateAction<boolean>>;
  setShareInfo: React.Dispatch<React.SetStateAction<{ id: string } | null>>;
  batchSelected: Set<string>;
  setBatchSelected: React.Dispatch<React.SetStateAction<Set<string>>>;
  setBatchProcessing: React.Dispatch<React.SetStateAction<boolean>>;
  loadTemplates: () => Promise<void>;
}

/* ================================================================
   Hook
   ================================================================ */

export function useDashboardActions(deps: ActionDeps) {
  const navigate = useNavigate();
  const {
    user,
    templates,
    setTemplates,
    setActionLoading,
    setDeleteConfirm,
    setEditingId,
    setShowCreateForm,
    setShareInfo,
    batchSelected,
    setBatchSelected,
    setBatchProcessing,
    loadTemplates,
  } = deps;

  // ---- Batch helpers ----
  const clearBatchSelection = useCallback(() => setBatchSelected(new Set()), [setBatchSelected]);

  const toggleBatchSelect = useCallback((id: string) => {
    setBatchSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, [setBatchSelected]);

  // ---- Create ----
  const handleCreate = useCallback(async (
    meta: Omit<TemplateMeta, "id" | "createdAt" | "updatedAt">,
    masterSourceId?: string,
  ) => {
    setActionLoading("create");
    try {
      let template: TemplateMeta;
      if (masterSourceId) {
        template = await duplicateTemplate(masterSourceId, { ...meta, isMaster: false, masterSourceId });
      } else {
        template = await createTemplate({ ...meta, isMaster: false });
      }
      setTemplates((prev) => [template, ...prev]);
      setShowCreateForm(false);
      navigate(`/t/${template.id}/`);
    } catch (err) {
      console.error("[Dashboard] Failed to create template:", err);
      toast.error("\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D9\u05E6\u05D9\u05E8\u05EA \u05EA\u05D1\u05E0\u05D9\u05D5\u05EA");
    } finally {
      setActionLoading(null);
    }
  }, [navigate, setTemplates, setActionLoading, setShowCreateForm]);

  // ---- Duplicate ----
  const handleDuplicate = useCallback(async (source: TemplateMeta) => {
    setActionLoading(`dup-${source.id}`);
    try {
      const newMeta = {
        templateType: source.templateType || ("branding" as const),
        clientName: source.clientName + " (\u05E2\u05D5\u05EA\u05E7)",
        clientNumber: source.clientNumber,
        characterizer: source.characterizer,
        marketer: source.marketer,
        designer: source.designer,
        developer: source.developer,
        contentWriter: source.contentWriter,
        tags: source.tags,
      };
      const template = await duplicateTemplate(source.id, newMeta);
      setTemplates((prev) => [template, ...prev]);
    } catch (err) {
      console.error("[Dashboard] Failed to duplicate template:", err);
      toast.error("\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05E9\u05DB\u05E4\u05D5\u05DC \u05EA\u05D1\u05E0\u05D9\u05D5\u05EA");
    } finally {
      setActionLoading(null);
    }
  }, [setTemplates, setActionLoading]);

  // ---- Delete ----
  const handleDelete = useCallback(async (id: string) => {
    setActionLoading(`del-${id}`);
    try {
      await deleteTemplate(id);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      setDeleteConfirm(null);
    } catch (err) {
      console.error("[Dashboard] Failed to delete template:", err);
      toast.error("\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05DE\u05D7\u05D9\u05E7\u05EA \u05EA\u05D1\u05E0\u05D9\u05D5\u05EA");
    } finally {
      setActionLoading(null);
    }
  }, [setTemplates, setActionLoading, setDeleteConfirm]);

  // ---- Update (with notification triggers) ----
  const handleUpdate = useCallback(async (
    id: string,
    updates: Partial<Omit<TemplateMeta, "id" | "createdAt">>,
  ) => {
    setActionLoading(`edit-${id}`);
    try {
      const oldTemplate = templates.find((t) => t.id === id);
      const updated = await updateTemplate(id, updates);
      setTemplates((prev) => prev.map((t) => (t.id === id ? updated : t)));
      setEditingId(null);

      // --- Send notifications for changes (fire-and-forget) ---
      if (oldTemplate && user) {
        const tplName = updated.clientName || id;
        const fromUser = user.displayName || user.username;

        const assignFields: { field: keyof TemplateMeta; label: string }[] = [
          { field: "characterizer", label: "\u05DE\u05D0\u05E4\u05D9\u05D9\u05E0\u05EA" },
          { field: "designer", label: "\u05DE\u05E2\u05E6\u05D1/\u05EA" },
          { field: "contentWriter", label: "\u05DB\u05D5\u05EA\u05D1/\u05EA \u05EA\u05D5\u05DB\u05DF" },
          { field: "developer", label: "\u05DE\u05E4\u05EA\u05D7/\u05EA" },
          { field: "marketer", label: "\u05DE\u05E7\u05D3\u05DE/\u05EA" },
        ];
        for (const { field, label } of assignFields) {
          const oldVal = (oldTemplate as any)[field] as string | undefined;
          const newVal = (updated as any)[field] as string | undefined;
          if (newVal && newVal !== oldVal) {
            sendNotification(newVal, {
              type: "assignment",
              title: `\u05D4\u05D5\u05E7\u05E6\u05D9\u05EA \u05DC\u05EA\u05D1\u05E0\u05D9\u05D5\u05EA "${tplName}"`,
              message: `\u05D4\u05D5\u05E7\u05E6\u05D9\u05EA \u05DB${label} \u05DC\u05EA\u05D1\u05E0\u05D9\u05D5\u05EA "${tplName}" \u05E2\u05DC \u05D9\u05D3\u05D9 ${fromUser}.`,
              templateId: id,
              templateName: tplName,
              link: `/t/${id}/`,
              fromUser,
            }).catch(() => {});
          }
        }

        if (updates.status && updates.status !== oldTemplate.status) {
          const statusLabel = STATUS_CONFIG[updates.status as keyof typeof STATUS_CONFIG]?.label || updates.status;
          const teamMembers = new Set<string>();
          for (const { field } of assignFields) {
            const v = (updated as any)[field] as string | undefined;
            if (v && v !== user.username) teamMembers.add(v);
          }
          for (const member of teamMembers) {
            sendNotification(member, {
              type: "status_change",
              title: `\u05E1\u05D8\u05D8\u05D5\u05E1 \u05E9\u05D5\u05E0\u05D4: "${tplName}"`,
              message: `\u05D4\u05E1\u05D8\u05D8\u05D5\u05E1 \u05E9\u05DC "${tplName}" \u05E9\u05D5\u05E0\u05D4 \u05DC-${statusLabel} \u05E2\u05DC \u05D9\u05D3\u05D9 ${fromUser}.`,
              templateId: id,
              templateName: tplName,
              link: `/t/${id}/`,
              fromUser,
            }).catch(() => {});
          }
        }
      }
    } catch (err) {
      console.error("[Dashboard] Failed to update template:", err);
      toast.error("\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05E2\u05D3\u05DB\u05D5\u05DF \u05EA\u05D1\u05E0\u05D9\u05D5\u05EA");
    } finally {
      setActionLoading(null);
    }
  }, [templates, user, setTemplates, setActionLoading, setEditingId]);

  // ---- Share ----
  const handleShare = useCallback((id: string) => {
    setShareInfo({ id });
  }, [setShareInfo]);

  // ---- Archive ----
  const handleArchive = useCallback(async (id: string) => {
    setActionLoading(`arc-${id}`);
    try {
      const updated = await archiveTemplate(id);
      setTemplates((prev) => prev.map((t) => (t.id === id ? updated : t)));
      toast.success("\u05D4\u05EA\u05D1\u05E0\u05D9\u05EA \u05D4\u05D5\u05E2\u05D1\u05E8\u05D4 \u05DC\u05D0\u05E8\u05DB\u05D9\u05D5\u05DF");
    } catch (err) {
      console.error("[Dashboard] Failed to archive template:", err);
      toast.error("\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D4\u05E2\u05D1\u05E8\u05D4 \u05DC\u05D0\u05E8\u05DB\u05D9\u05D5\u05DF");
    } finally {
      setActionLoading(null);
    }
  }, [setTemplates, setActionLoading]);

  // ---- Restore ----
  const handleRestore = useCallback(async (id: string) => {
    setActionLoading(`rst-${id}`);
    try {
      const updated = await restoreTemplate(id);
      setTemplates((prev) => prev.map((t) => (t.id === id ? updated : t)));
      toast.success("\u05D4\u05EA\u05D1\u05E0\u05D9\u05EA \u05E9\u05D5\u05D7\u05D6\u05E8\u05D4 \u05DE\u05D4\u05D0\u05E8\u05DB\u05D9\u05D5\u05DF");
    } catch (err) {
      console.error("[Dashboard] Failed to restore template:", err);
      toast.error("\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05E9\u05D7\u05D6\u05D5\u05E8 \u05DE\u05D0\u05E8\u05DB\u05D9\u05D5\u05DF");
    } finally {
      setActionLoading(null);
    }
  }, [setTemplates, setActionLoading]);

  // ---- Batch delete ----
  const handleBatchDelete = useCallback(async () => {
    const ids = [...batchSelected];
    if (ids.length === 0) return;
    setBatchProcessing(true);
    try {
      for (const id of ids) {
        await deleteTemplate(id);
      }
      setTemplates((prev) => prev.filter((t) => !batchSelected.has(t.id)));
      clearBatchSelection();
      toast.success(`${ids.length} \u05EA\u05D1\u05E0\u05D9\u05D5\u05EA \u05E0\u05DE\u05D7\u05E7\u05D5`);
    } catch (err) {
      console.error("[Dashboard] Batch delete failed:", err);
      toast.error("\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05DE\u05D7\u05D9\u05E7\u05D4 \u05DE\u05E8\u05D5\u05DB\u05D6\u05EA");
    } finally {
      setBatchProcessing(false);
    }
  }, [batchSelected, clearBatchSelection, setTemplates, setBatchProcessing]);

  // ---- Batch status change ----
  const handleBatchStatusChange = useCallback(async (status: TemplateStatus) => {
    const ids = [...batchSelected];
    if (ids.length === 0) return;
    setBatchProcessing(true);
    try {
      const updatedList: TemplateMeta[] = [];
      for (const id of ids) {
        const updated = await updateTemplate(id, { status });
        updatedList.push(updated);
      }
      setTemplates((prev) =>
        prev.map((t) => {
          const upd = updatedList.find((u) => u.id === t.id);
          return upd || t;
        }),
      );
      clearBatchSelection();
      toast.success(`\u05E1\u05D8\u05D8\u05D5\u05E1 \u05E2\u05D5\u05D3\u05DB\u05DF \u05DC-${ids.length} \u05EA\u05D1\u05E0\u05D9\u05D5\u05EA`);
    } catch (err) {
      console.error("[Dashboard] Batch status change failed:", err);
      toast.error("\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05E2\u05D3\u05DB\u05D5\u05DF \u05E1\u05D8\u05D8\u05D5\u05E1");
    } finally {
      setBatchProcessing(false);
    }
  }, [batchSelected, clearBatchSelection, setTemplates, setBatchProcessing]);

  // ---- Batch add tag ----
  const handleBatchAddTag = useCallback(async (tag: string) => {
    const ids = [...batchSelected];
    if (ids.length === 0) return;
    setBatchProcessing(true);
    try {
      const updatedList: TemplateMeta[] = [];
      for (const id of ids) {
        const existing = templates.find((t) => t.id === id);
        if (!existing) continue;
        const currentTags = existing.tags || [];
        if (currentTags.includes(tag)) continue;
        const updated = await updateTemplate(id, { tags: [...currentTags, tag] });
        updatedList.push(updated);
      }
      if (updatedList.length > 0) {
        setTemplates((prev) =>
          prev.map((t) => {
            const upd = updatedList.find((u) => u.id === t.id);
            return upd || t;
          }),
        );
      }
      clearBatchSelection();
      toast.success(`\u05EA\u05D2\u05D9\u05EA "${tag}" \u05E0\u05D5\u05E1\u05E4\u05D4 \u05DC-${updatedList.length} \u05EA\u05D1\u05E0\u05D9\u05D5\u05EA`);
    } catch (err) {
      console.error("[Dashboard] Batch add tag failed:", err);
      toast.error("\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D4\u05D5\u05E1\u05E4\u05EA \u05EA\u05D2\u05D9\u05EA");
    } finally {
      setBatchProcessing(false);
    }
  }, [batchSelected, templates, clearBatchSelection, setTemplates, setBatchProcessing]);

  // ---- Export template as JSON ----
  const handleExport = useCallback(async (id: string) => {
    setActionLoading(`export-${id}`);
    try {
      const { authFetch } = await import("../utils/apiClient");
      const res = await authFetch(`/templates/export/${id}`, { method: "POST" });
      if (!res.ok || !res.data) throw new Error(res.error || "Export failed");

      const json = JSON.stringify(res.data, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = (res.data.meta?.clientName || id).replace(/[^a-zA-Z0-9\u0590-\u05FF_-]/g, "_");
      a.download = `${safeName}.zap.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("התבנית יוצאה בהצלחה");
    } catch (err) {
      console.error("[Dashboard] Export failed:", err);
      toast.error("שגיאה בייצוא התבנית");
    } finally {
      setActionLoading(null);
    }
  }, [setActionLoading]);

  // ---- Import template from JSON file ----
  const handleImport = useCallback(async (file: File) => {
    setActionLoading("import");
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (data._format !== "zap-web-template-export") {
        toast.error("קובץ לא תקין — נדרש קובץ .zap.json");
        return;
      }
      const { authFetch } = await import("../utils/apiClient");
      const res = await authFetch("/templates/import", {
        method: "POST",
        body: JSON.stringify({ data }),
      });
      if (!res.ok) throw new Error(res.error || "Import failed");

      invalidateTemplatesCache();
      await loadTemplates();
      toast.success(`התבנית יובאה בהצלחה (${res.contentCount} פריטי תוכן)`);
    } catch (err) {
      console.error("[Dashboard] Import failed:", err);
      toast.error("שגיאה בייבוא התבנית");
    } finally {
      setActionLoading(null);
    }
  }, [setActionLoading, loadTemplates]);

  return {
    navigate,
    handleCreate,
    handleDuplicate,
    handleDelete,
    handleUpdate,
    handleShare,
    handleArchive,
    handleRestore,
    handleExport,
    handleImport,
    handleBatchDelete,
    handleBatchStatusChange,
    handleBatchAddTag,
    toggleBatchSelect,
    clearBatchSelection,
  };
}